import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth, formatApiError } from "../context/AuthContext";
import { PageShell } from "../components/PageShell";

export const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(location.hash.slice(1)).get("session_id");
    api.post("/auth/google/session", { session_id: sid })
      .then(({ data }) => { setUser(data); toast.success(`Welcome, ${data.name || "traveler"}.`); })
      .catch(() => toast.error("Google sign-in failed."))
      .finally(() => navigate(localStorage.getItem("rhf_next") || "/", { replace: true }));
  }, [location.hash, navigate, setUser]);
  return null;
};

export default function Login() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const next = new URLSearchParams(useLocation().search).get("next") || "/";
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (user) navigate(next, { replace: true }); }, [user, next, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/auth/${mode === "login" ? "login" : "register"}`, form);
      setUser(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const google = () => {
    localStorage.setItem("rhf_next", next);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const field = "w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-cyan-400/60";
  return (
    <PageShell back="/" backLabel="Home" label="Account" title="Save your multiverse" subtitle="An account keeps your timelines, conversations and Premium across devices. Guest mode still works without one.">
      <form onSubmit={submit} className="glass rounded-3xl p-8 max-w-md space-y-4" data-testid="auth-form">
        <div className="flex gap-2 mb-2">
          {["login", "register"].map((m) => (
            <button type="button" key={m} onClick={() => setMode(m)} data-testid={`auth-tab-${m}`}
              className={`btn-pill text-xs ${mode === m ? "btn-pill-primary" : ""}`}>{m === "login" ? "Sign in" : "Create account"}</button>
          ))}
        </div>
        {mode === "register" && <input className={field} placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="auth-name" />}
        <input className={field} type="email" required placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="auth-email" />
        <input className={field} type="password" required minLength={8} placeholder="Password (8+ characters)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="auth-password" />
        <button disabled={busy} className="btn-pill btn-pill-primary w-full justify-center" data-testid="auth-submit">{busy ? "One moment…" : mode === "login" ? "Sign in" : "Create account"}</button>
        <div className="text-center text-xs text-white/40">or</div>
        <button type="button" onClick={google} className="btn-pill w-full justify-center" data-testid="auth-google">Continue with Google</button>
        <Link to="/" className="block text-center text-xs text-white/40 hover:text-white/70" data-testid="auth-guest">Continue as guest</Link>
      </form>
    </PageShell>
  );
}
